var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { SignalWatcher } from "@lit-labs/preact-signals";
import "../components/code-input";
import { draft } from "../state";
function isDx(c) {
    return c.classification === "icd10am" || c.type === "Pdx" || c.type === "Adx";
}
function isProc(c) {
    return c.classification === "achi" || c.type === "Pproc" || c.type === "Aproc";
}
let ModeAbstract = class ModeAbstract extends SignalWatcher(LitElement) {
    constructor() {
        super(...arguments);
        this.edition = "13e";
        // Which episode-detail fields this case actually assesses (contract 3); defaults
        // to "show everything" so an older payload without the field doesn't hide the panel.
        this.episodePanel = { hmv: true, weight: true, query: true };
        this.dragSection = null;
        this.dragFrom = -1;
    }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    :host { display: block; }
    .intro h2 { font-family: var(--font-display); font-size: 13px; font-weight: 700; margin: 0 0 3px;
                text-transform: uppercase; letter-spacing: .05em; color: var(--color-slate-700); }
    .intro .sub { font-size: 12px; color: var(--color-slate-500); margin-bottom: 14px; line-height: 1.45; }
    .section { border: 1px solid var(--color-slate-200); border-radius: var(--radius-md);
               background: var(--color-white); margin-bottom: 14px; box-shadow: var(--shadow-sm);
               overflow: hidden; }
    .section-head { padding: 11px 13px; background: var(--color-slate-50);
                    border-bottom: 1px solid var(--color-slate-200); }
    .section-head h3 { margin: 0; font-size: 12px; text-transform: uppercase; letter-spacing: .05em;
                       color: var(--color-slate-700); font-weight: 700; display: flex; align-items: center; gap: 7px; }
    .section-head .count { color: var(--color-white); background: var(--color-slate-400); font-weight: 700;
                           font-size: 11px; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
                           display: inline-flex; align-items: center; justify-content: center; }
    .section-head .sub { margin: 3px 0 0; font-size: 11px; color: var(--color-slate-500); }
    .search { padding: 11px 13px; }
    .cards { padding: 4px 13px 13px; }
    .empty-hint { font-size: 12px; color: var(--color-slate-400); font-style: italic; padding: 2px 0 6px; line-height: 1.5; }
    /* code column is snug per section (ICD ≤7 chars, ACHI up to 8 like 13703-00).
       minmax(0,1fr) + min-width:0 on the text cell lets the row shrink so the
       remove (×) button stays inside the card even when the dx meta (Onset
       select + DCID) is wide — without it the × spilled past the dx card. */
    .card { display: grid; grid-template-columns: 18px 52px minmax(0, 1fr) auto; column-gap: 8px;
            align-items: start; padding: 10px; background: var(--color-white);
            border: 1px solid var(--color-slate-200); border-radius: var(--radius-md);
            margin-bottom: 6px; cursor: grab; }
    .card.sec-proc { grid-template-columns: 18px 66px minmax(0, 1fr) auto; }
    .card > div { min-width: 0; }
    .card.drop-target { box-shadow: 0 -2px 0 0 var(--color-blue-600); }
    .handle { color: var(--color-slate-400); cursor: grab; font-size: 14px; user-select: none; padding-top: 1px; }
    .code { font-family: var(--font-mono); font-weight: 700; color: var(--color-slate-900);
            font-size: 13px; padding-top: 1px; }
    .desc { color: var(--color-slate-700); font-size: 13px; line-height: 1.3; }
    .meta { font-size: 11px; color: var(--color-slate-500); margin-top: 6px; display: flex; gap: 14px;
            align-items: center; flex-wrap: wrap; }
    .meta label { display: inline-flex; align-items: center; gap: 5px; }
    .meta select { padding: 1px 4px; border-radius: 3px; border: 1px solid var(--color-slate-200);
                   font: inherit; font-size: 11px; color: var(--color-slate-700); background: white;
                   max-width: 100%; }
    .meta input { width: 48px; padding: 1px 4px; border-radius: 3px; border: 1px solid var(--color-slate-200);
                  font: inherit; font-size: 11px; font-family: var(--font-mono); color: var(--color-slate-700);
                  text-align: center; text-transform: uppercase; }
    .role-badge { display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 10px;
                  letter-spacing: .04em; text-transform: uppercase; margin-right: 6px; vertical-align: 1px; }
    .role-principal { background: var(--color-blue-600); color: white; }
    .role-additional { background: var(--color-slate-100); color: var(--color-slate-600); }
    button.rm { background: transparent; border: 0; color: var(--color-slate-400); cursor: pointer;
                font-size: 16px; line-height: 1; padding: 2px 4px; border-radius: 3px; }
    button.rm:hover { background: #FEF2F2; color: #DC2626; }
    .card-actions { display: flex; align-items: center; gap: 4px; }
    .arrows { display: flex; gap: 2px; }
    .arrows button { padding: 2px 6px; cursor: pointer; border: 1px solid var(--color-slate-300);
                     background: var(--color-white); border-radius: var(--radius-sm); font-size: 12px; }
    .arrows button:hover { background: var(--color-slate-50); }
    .ed-section { border: 1px solid var(--color-slate-200); border-radius: var(--radius-md); overflow: hidden; }
    .ed-head { padding: 10px 12px; background: var(--color-slate-50); border-bottom: 1px solid var(--color-slate-200);
               font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em;
               color: var(--color-slate-700); }
    .ed-grid { padding: 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; }
    .ed-field { display: flex; flex-direction: column; gap: 3px; }
    .ed-field > label { font-size: 11px; color: var(--color-slate-500); text-transform: uppercase;
                        letter-spacing: .04em; font-weight: 600; }
    .ed-field .hint { font-size: 11px; color: var(--color-slate-400); font-weight: 400;
                      text-transform: none; letter-spacing: 0; }
    .ed-field input[type=number], .ed-field textarea { padding: 6px 8px; border-radius: var(--radius-sm);
        border: 1px solid var(--color-slate-300); font: inherit; font-size: 13px; }
    .ed-field textarea { min-height: 92px; resize: vertical; font-family: var(--font-base); line-height: 1.45; }
    .ed-field.checkbox { flex-direction: row; align-items: center; gap: 8px; grid-column: 1 / -1; }
    .ed-field.checkbox label { font-size: 13px; color: var(--color-slate-700); font-weight: 400;
                               text-transform: none; letter-spacing: 0; }
  `; }
    // ----- helpers over draft.codes -----
    dxCards() { return (draft.value?.codes ?? []).filter(isDx); }
    procCards() { return (draft.value?.codes ?? []).filter(isProc); }
    /** Rebuild codes[] as [dx..., proc...] and derive type by position in each section. */
    commit(dx, proc) {
        const d = draft.value;
        const typedDx = dx.map((c, i) => ({ ...c, classification: "icd10am", type: i === 0 ? "Pdx" : "Adx" }));
        const typedProc = proc.map((c, i) => ({ ...c, classification: "achi", type: i === 0 ? "Pproc" : "Aproc" }));
        draft.value = { ...d, codes: [...typedDx, ...typedProc] };
    }
    onPick(e, section) {
        const { code, description } = e.detail;
        const dx = this.dxCards();
        const proc = this.procCards();
        const target = section === "dx" ? dx : proc;
        if (target.some((c) => c.code === code))
            return;
        const card = section === "dx"
            ? { code, description, cof: 2, dcid: "8", classification: "icd10am", type: "Adx" }
            : { code, description, classification: "achi", type: "Aproc" };
        target.push(card);
        this.commit(dx, proc);
    }
    removeCode(code) {
        this.commit(this.dxCards().filter((c) => c.code !== code), this.procCards().filter((c) => c.code !== code));
    }
    setField(code, field, value) {
        const apply = (c) => (c.code === code ? { ...c, [field]: value } : c);
        this.commit(this.dxCards().map(apply), this.procCards().map(apply));
    }
    /** Move a card up/down within its section (arrow-button alternative to drag). */
    move(section, code, delta) {
        const list = section === "dx" ? this.dxCards() : this.procCards();
        const i = list.findIndex((c) => c.code === code);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= list.length)
            return;
        [list[i], list[j]] = [list[j], list[i]];
        if (section === "dx")
            this.commit(list, this.procCards());
        else
            this.commit(this.dxCards(), list);
    }
    // ----- drag-drop within a section -----
    onDragStart(section, i) { this.dragSection = section; this.dragFrom = i; }
    onDragEnd() { this.dragSection = null; this.dragFrom = -1; }
    onDrop(section, to) {
        if (this.dragSection !== section || this.dragFrom < 0 || this.dragFrom === to)
            return;
        const list = section === "dx" ? this.dxCards() : this.procCards();
        const [moved] = list.splice(this.dragFrom, 1);
        list.splice(to, 0, moved);
        if (section === "dx")
            this.commit(list, this.procCards());
        else
            this.commit(this.dxCards(), list);
    }
    setMeta(key, value) {
        const d = draft.value;
        draft.value = { ...d, episodeMeta: { ...d.episodeMeta, [key]: value } };
    }
    renderCard(c, i, section) {
        const isP = i === 0;
        const badge = isP
            ? html `<span class="role-badge role-principal">${section === "dx" ? "Principal Dx" : "Principal Proc"}</span>`
            : html `<span class="role-badge role-additional">${section === "dx" ? "Additional Dx" : "Additional Proc"} ${i + 1}</span>`;
        const meta = section === "dx"
            ? html `<div class="meta">
          <label>Onset
            <select aria-label="Onset (COF)" .value=${String(c.cof ?? 2)}
                    @change=${(e) => this.setField(c.code, "cof", parseInt(e.target.value, 10))}>
              <option value="2">Pre-existing (COF 2)</option>
              <option value="1">Arose this admission (COF 1)</option>
            </select></label>
          <label title="Diagnostic Cluster ID: A to Z and/or 0 to 9. Default 8; edit if grouping related conditions.">DCID
            <input type="text" maxlength="4" aria-label="DCID" .value=${c.dcid ?? ""}
                   @input=${(e) => {
                const el = e.target;
                const clean = el.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
                if (clean !== el.value)
                    el.value = clean;
                this.setField(c.code, "dcid", clean || undefined);
            }} /></label>
        </div>`
            : html `<div class="meta">
          <label title="Procedure occurrences in the episode.">Occurrences
            <input type="number" min="1" step="1" aria-label="Occurrences" .value=${String(c.occurrences ?? "")}
                   @change=${(e) => {
                const v = e.target.value.trim();
                this.setField(c.code, "occurrences", v === "" ? undefined : parseInt(v, 10));
            }} /></label>
        </div>`;
        return html `
      <div class="card sec-${section}" draggable="true"
           @dragstart=${() => this.onDragStart(section, i)}
           @dragend=${() => this.onDragEnd()}
           @dragover=${(e) => { if (this.dragSection === section)
            e.preventDefault(); }}
           @drop=${(e) => { e.preventDefault(); this.onDrop(section, i); }}>
        <span class="handle" title="Drag to reorder">⋮⋮</span>
        <span class="code">${c.code}</span>
        <div><div class="desc">${badge}${c.description ?? ""}</div>${meta}</div>
        <div class="card-actions">
          <div class="arrows">
            <button type="button" data-up=${c.code} aria-label="Move ${c.code} up" @click=${() => this.move(section, c.code, -1)}>▲</button>
            <button type="button" data-down=${c.code} aria-label="Move ${c.code} down" @click=${() => this.move(section, c.code, 1)}>▼</button>
          </div>
          <button class="rm" title="Remove" data-rm=${c.code} @click=${() => this.removeCode(c.code)}>×</button>
        </div>
      </div>`;
    }
    renderSection(label, section, cls, cards, placeholder) {
        return html `
      <div class="section">
        <div class="section-head">
          <h3>${label} <span class="count">(${cards.length})</span></h3>
          <div class="sub">Type the ${cls === "icd10am" ? "ICD-10-AM" : "ACHI"} code you have assigned. Drag rows to reorder.</div>
        </div>
        <div class="search">
          <code-input .edition=${this.edition} classification=${cls} placeholder=${placeholder}
                      @code-pick=${(e) => this.onPick(e, section)}></code-input>
        </div>
        <div class="cards">
          ${cards.length === 0
            ? html `<div class="empty-hint">No ${label.toLowerCase()} yet.${section === "proc" ? " Leave empty if none performed." : ""}</div>`
            : cards.map((c, i) => this.renderCard(c, i, section))}
        </div>
      </div>`;
    }
    render() {
        const em = draft.value?.episodeMeta ?? {};
        const queryRaised = !!em.coder_clinician_query_required;
        const ep = this.episodePanel;
        const showEpisodeSection = ep.hmv || ep.weight || ep.query;
        return html `
      <div class="intro">
        <h2>Episode coding</h2>
        <div class="sub">Sequence reflects clinical priority. First in each section becomes the Principal.</div>
      </div>
      ${this.renderSection("Diagnoses", "dx", "icd10am", this.dxCards(), "ICD-10-AM code (e.g. E11.71)")}
      ${this.renderSection("Procedures", "proc", "achi", this.procCards(), "ACHI code (e.g. 30473-00)")}
      ${showEpisodeSection ? html `
        <div class="ed-section">
          <div class="ed-head">Episode details</div>
          <div class="ed-grid">
            ${ep.weight ? html `
              <div class="ed-field">
                <label>Weight at admission<div class="hint">grams · neonate / paediatric only</div></label>
                <input type="number" min="0" step="1" aria-label="Weight at admission (g)"
                       .value=${String(em.weight_at_admission_g ?? "")}
                       @change=${(e) => {
            const v = e.target.value.trim();
            this.setMeta("weight_at_admission_g", v === "" ? null : parseInt(v, 10));
        }} />
              </div>` : ""}
            ${ep.hmv ? html `
              <div class="ed-field">
                <label>HMV<div class="hint">total hours of mechanical ventilation</div></label>
                <input type="number" min="0" step="1" aria-label="HMV hours"
                       .value=${String(em.hmv_hours ?? "")}
                       @change=${(e) => {
            const v = e.target.value.trim();
            this.setMeta("hmv_hours", v === "" ? null : parseInt(v, 10));
        }} />
              </div>` : ""}
            ${ep.query ? html `
              <div class="ed-field checkbox">
                <input type="checkbox" id="cq" .checked=${queryRaised}
                       @change=${(e) => this.setMeta("coder_clinician_query_required", e.target.checked)} />
                <label for="cq">Coder-clinician query</label>
              </div>
              ${queryRaised ? html `
                <div class="ed-field" style="grid-column:1/-1;">
                  <label>Query text<div class="hint">Address the treating clinician; keep it clear, concise and non-leading.</div></label>
                  <textarea aria-label="Query text" rows="5"
                            placeholder="Do you...&#10;(reference the specific note, date and finding; ask an open question, do not suggest the diagnosis or code)"
                            .value=${em.coder_clinician_query_note ?? ""}
                            @input=${(e) => this.setMeta("coder_clinician_query_note", e.target.value)}></textarea>
                </div>` : ""}` : ""}
          </div>
        </div>` : ""}
    `;
    }
};
__decorate([
    property({ type: String })
], ModeAbstract.prototype, "edition", void 0);
__decorate([
    property({ attribute: false })
], ModeAbstract.prototype, "episodePanel", void 0);
__decorate([
    state()
], ModeAbstract.prototype, "dragSection", void 0);
__decorate([
    state()
], ModeAbstract.prototype, "dragFrom", void 0);
ModeAbstract = __decorate([
    customElement("mode-abstract")
], ModeAbstract);
export { ModeAbstract };
